import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Checkbox, CircularProgress, FormControlLabel, Tooltip } from '@mui/material';
import { has } from 'lodash';
import classNames from 'classnames';
import Typography from 'new/elements/Typography';
import { getDataFeedParams } from './getDataFeedParams';
import {
  FeedField,
  FeedParamsType,
  SelectedFeedsType,
  VendorFeedSpecs,
} from 'new/components/flows/types/elt/CreateELTFlow/types';
import { createVendorFeedSpecsWrapper } from './utils/vendorFeedSpecsUtils';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import classes from './SourceSetupPage.module.scss';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { ChevronRight } from '@mui/icons-material';
import useLoadFields from './useLoadFields';
import FeedFieldItem from './FeedFieldItem';
import { useSelector } from 'react-redux';

// V2 components removed - now using unified approach with VendorFeedSpecsWrapper

interface FeedItemProps {
  label: string;
  value: string;
  formType: string;
  vendorFeedSpecs: VendorFeedSpecs;
  selectedFeeds: SelectedFeedsType;
  enableFieldSelection: boolean;
  onFeedSelectorChanged: (feed: string, params: any) => void;
  onFeedFieldSelectorChanged: ({
    feed,
    field,
    isChecked,
    allFields,
    feedParams,
  }: {
    feed: string;
    field: string;
    isChecked: boolean;
    allFields: FeedField[];
    feedParams: FeedParamsType;
  }) => void;
  onSelectAllFields: ({
    feed,
    fieldValues,
    isChecked,
    allFields,
  }: {
    feed: string;
    fieldValues: string[];
    isChecked: boolean;
    allFields: FeedField[];
  }) => void;
  onFeedPropsChanged: (feed: string, props: any) => void;
  onExpandFeed: (feed: string, isExpanded: boolean) => void;
  templateVersionInfo?: {
    templateVersion: 'v1' | 'v2';
    isV2Template: boolean;
  };
}

const FeedItem: React.FC<FeedItemProps> = ({
  label,
  value,
  formType,
  enableFieldSelection,
  vendorFeedSpecs,
  selectedFeeds,
  onFeedSelectorChanged,
  onFeedPropsChanged,
  onFeedFieldSelectorChanged,
  onSelectAllFields,
  onExpandFeed,
  templateVersionInfo,
}) => {
  const { loadFields, isLoading, fields } = useLoadFields(formType, value);

  const fieldsRef = useRef(fields);

  const isExpanded = useSelector(
    (state: any) => state.forms[formType]?.data?.expandedFeeds?.[value],
  );

  // Create wrapper for type-safe access to vendor feed specs
  const vendorFeedSpecsWrapper = useMemo(() => {
    return createVendorFeedSpecsWrapper(
      vendorFeedSpecs,
      templateVersionInfo?.templateVersion || 'v1',
    );
  }, [vendorFeedSpecs, templateVersionInfo?.templateVersion]);

  if (!vendorFeedSpecs?.data_feed_options[value]) {
    return null;
  }

  const id = `feed-checkbox-${value}`;
  const currentFeedSettings = selectedFeeds?.[value];
  const thisFeedOptions = vendorFeedSpecs.data_feed_options[value];
  const feedParams: FeedParamsType = getDataFeedParams(
    selectedFeeds,
    value,
    vendorFeedSpecs,
    templateVersionInfo?.templateVersion,
  );

  // Get feed choices using the wrapper for unified v1/v2 handling
  const feedChoicesSelectOptions = useMemo(() => {
    if (vendorFeedSpecsWrapper.isV2Template()) {
      // For V2: Show strategy names but use ref_feed values
      const v2Specs = vendorFeedSpecs as any;
      const strategies = v2Specs.ref_feed_options?.strategies || {};
      const strategyMappings = v2Specs.strategyMappings || {};
      const thisFeedOptions = v2Specs.data_feed_options?.[value];
      const allowedRefFeeds = thisFeedOptions?.allowed_reference_feeds || [];

      // For each strategy, find which ref_feeds are allowed for this specific feed
      return Object.entries(strategies)
        .map(([strategyKey, strategyInfo]: [string, any]) => {
          const strategyName = strategyInfo?.label || strategyKey;
          const strategyRefFeeds = strategyMappings[strategyKey]?.ref_feeds || [];

          // Find the first ref_feed from this strategy that is allowed for this feed
          const allowedRefFeed = strategyRefFeeds.find((refFeed: string) =>
            allowedRefFeeds.includes(refFeed),
          );

          // Only include this strategy if it has at least one allowed ref_feed for this feed
          if (allowedRefFeed) {
            return {
              label: strategyName,
              value: allowedRefFeed, // Save the specific ref_feed value for this feed
            };
          }
          return null;
        })
        .filter(Boolean);
    } else {
      // For V1: Direct mapping as before
      const refFeedSelectors = vendorFeedSpecsWrapper.getRefFeedSelectors(value);
      return refFeedSelectors.map((refFeed) => ({
        label: vendorFeedSpecsWrapper.getDisplayLabel(refFeed),
        value: refFeed,
      }));
    }
  }, [vendorFeedSpecsWrapper, value, vendorFeedSpecs]);

  const currentRefFeed = has(selectedFeeds, value)
    ? selectedFeeds[value]?.ref_spec_name || thisFeedOptions?.default_ref
    : thisFeedOptions?.default_ref;

  const currentRefFeedOption = getCurrentSelectedOption(feedChoicesSelectOptions, currentRefFeed);

  // Get primary key using the wrapper for unified v1/v2 handling
  const primaryKeyView = vendorFeedSpecsWrapper.getPrimaryKey(value);

  if (feedParams.ref_spec_name === '' || feedParams.ref_spec_resource_name === '') {
    return null;
  }

  const isSelected = !!currentFeedSettings?.feed_selected;
  const isIndeterminate =
    isSelected &&
    Object.keys(currentFeedSettings?.selectedFields || {}).length <
      (currentFeedSettings?.allFields?.length ||
        currentFeedSettings?.allFieldsLength ||
        fields?.length ||
        0);

  useEffect(() => {
    if (fields !== fieldsRef.current) {
      if (isSelected && Object.keys(currentFeedSettings?.selectedFields || {}).length === 0) {
        onSelectAllFields({
          feed: value,
          fieldValues: fields.map((field) => field.value),
          isChecked: true,
          allFields: fields,
        });
      }
      fieldsRef.current = fields;
    }
  }, [fields.length, isSelected]);

  const handleExpand = async () => {
    onExpandFeed(value, !isExpanded);
    if (!isExpanded && fields.length === 0) {
      await loadFields();
    }
  };

  const onSelectField = (field: string, isChecked: boolean) => {
    onFeedFieldSelectorChanged({
      feedParams,
      allFields: fields,
      feed: value,
      field,
      isChecked,
    });
  };

  const onSelectFeed = (event, isChecked) => {
    if (event) {
      event.stopPropagation();
    }
    onFeedSelectorChanged(value, {
      isChecked: isChecked,
      feedLabel: label,
      allFields: fields,
      selectedFields: isChecked
        ? fields.reduce((acc, field) => ({ ...acc, [field.value]: true }), {})
        : {},
      feedParams,
    });
  };

  const itemSelectionDescription = (() => {
    if (
      isSelected &&
      (!(currentFeedSettings.allFields?.length || currentFeedSettings.allFieldsLength) ||
        (Object.keys(currentFeedSettings?.selectedFields || {}).length || 0) ===
          (currentFeedSettings?.allFields?.length || currentFeedSettings?.allFieldsLength || 0))
    ) {
      return 'All fields selected';
    }
    if (
      isSelected &&
      (currentFeedSettings.allFields?.length || currentFeedSettings.allFieldsLength)
    ) {
      return `${Object.keys(currentFeedSettings?.selectedFields || {}).length} / ${currentFeedSettings?.allFields?.length || currentFeedSettings?.allFieldsLength || 0} fields selected`;
    }
    return '';
  })();

  return (
    <div className={classes.feedsListItemContainer}>
      <div
        key={value}
        className={classNames(classes.feedsListItem, {
          [classes['feedsListItem_selected']]: isSelected === true,
          [classes.expanded]: isExpanded,
        })}
        onClick={() => onSelectFeed(null, !isSelected)}
        style={{ cursor: 'pointer' }}
      >
        <div className={classes.checkboxColumn}>
          <Checkbox
            id={id}
            checked={isSelected}
            indeterminate={isIndeterminate}
            onChange={(e, isChecked) => {
              e.stopPropagation();
              onSelectFeed(e, isChecked);
            }}
          />
          {enableFieldSelection && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleExpand();
              }}
              disabled={isLoading}
              style={{
                transform: isExpanded ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.2s ease',
              }}
            >
              <ChevronRight />
            </IconButton>
          )}
        </div>
        <div className={classes.feedTitle}>
          <label htmlFor={id} onClick={(e) => e.stopPropagation()}>
            <div className={classes.feedTitleContent}>
              <Typography
                variant="body1"
                component="div"
                className={`${classes.tableValue} ${classes.feedDisplayName}`}
              >
                {label}
                {isSelected && enableFieldSelection && itemSelectionDescription && (
                  <Typography
                    className={classes.feedFieldsCount}
                    variant="bodySmall"
                    component="div"
                  >
                    {itemSelectionDescription}
                  </Typography>
                )}
              </Typography>
            </div>
          </label>
        </div>
        <div className={classes.feedCode}>
          <Typography variant="body1" className={classes.tableValue}>
            {value}
          </Typography>
        </div>
        <div className={classes.feedChoices} onClick={(e) => e.stopPropagation()}>
          {isSelected && (
            <SortedSelect
              value={currentRefFeedOption}
              options={feedChoicesSelectOptions}
              components={{ Input: SelectInput, Option: SelectOption }}
              styles={{
                ...redesignStyles,
                dropdownIndicator: (styles, state) => ({
                  ...styles,
                  color: 'var(--color-greyscale-600)',
                  '&:hover': {
                    color: 'var(--color-greyscale-600)',
                  },
                }),
              }}
              isClearable={false}
              backspaceRemovesValue={false}
              isSearchable
              isDisabled={
                !has(selectedFeeds, value) ||
                !feedChoicesSelectOptions ||
                feedChoicesSelectOptions.length === 1
              }
              onChange={(option) => {
                let newValue = getNewSelectionValue(option, true);
                onFeedPropsChanged(value, {
                  ...thisFeedOptions,
                  ref_spec_name: newValue,
                });
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          )}
        </div>

        <div className={classes.feedPK}>{primaryKeyView}</div>
        <div
          className={classNames(classes.forceResyncColumn, {
            [classes.hidden]: !vendorFeedSpecsWrapper.supportsForceResync(currentRefFeed),
          })}
        >
          {isSelected && (
            <Tooltip title="Force resync will re-fetch all data from the source for this feed, even if it's already up-to-date.">
              <Checkbox
                checked={!!currentFeedSettings?.force_resync}
                onChange={(event, isChecked) => {
                  event.stopPropagation();
                  onFeedPropsChanged(value, {
                    force_resync: isChecked,
                  });
                }}
                size="small"
              />
            </Tooltip>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className={classes.fieldsList}>
          <div className={classes.fullWidthTable}>
            <div className={classes.tableHeader}>
              <div className={classes.tableHeaderRow}>
                <div className={classes.tableHeaderCell}>Field Name</div>
                <div className={classes.tableHeaderCell}>Field Value</div>
              </div>
            </div>
            <div className={classes.tableBody}>
              {isLoading && (
                <div className={classes.loading}>
                  <CircularProgress size={16} />
                </div>
              )}
              {fields.map((field) => (
                <FeedFieldItem
                  feed={value}
                  key={field.value}
                  label={field.label}
                  value={field.value}
                  vendorFeedSpecs={vendorFeedSpecs}
                  selectedFeeds={selectedFeeds}
                  onFeedFieldSelectorChanged={onSelectField}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedItem;
